import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiError } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { ActivationStepper } from "../components/ActivationStepper";
import { FormProgress } from "../components/FormProgress";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Pencil, UserCircle2, Eye, EyeOff, Trash2, Mail, Phone, MessageCircle, ShieldCheck, Send, Copy, CheckCircle2, XCircle, Clock, Sparkles, Layers, UserPlus, Calendar, Search } from "lucide-react";
import { FileUpload } from "../components/FileUpload";
import { PhoneInput } from "../components/PhoneInput";
import { USAddressFields } from "../components/USAddressFields";
import { useConfirm } from "../components/ConfirmDialog";
import { ROLE_LABEL, brl, formatUSPhone } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { SearchWithRecent } from "../components/SearchWithRecent";
import { titleCase } from "../lib/textCase";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { refreshSidebarCounts } from "../lib/sidebarBus";

const empty = {
  email: "",
  password: "",
  name: "",
  role: "collaborator",
  phone: "",
  ssn: "",
  position: "",
  valor_por_dia: 0,
  zelle_venmo: "",
  photo_path: "",
  status: "ativo",
  workdays: [1, 2, 3, 4, 5], // seg-sex padrão
  address: {
    street: "",
    apt_number: "",
    city: "",
    state: "",
    zip_code: "",
  },
};

const WEEKDAYS = [
  { id: 0, short: "Dom", label: "Domingo" },
  { id: 1, short: "Seg", label: "Segunda" },
  { id: 2, short: "Ter", label: "Terça" },
  { id: 3, short: "Qua", label: "Quarta" },
  { id: 4, short: "Qui", label: "Quinta" },
  { id: 5, short: "Sex", label: "Sexta" },
  { id: 6, short: "Sáb", label: "Sábado" },
];

const PERM_BADGES = [
  { key: "can_see_all_jobs", label: "Vê todas as casas" },
  { key: "can_start_anywhere", label: "Inicia s/ GPS" },
  { key: "can_finish_anywhere", label: "Finaliza s/ GPS" },
  { key: "can_reopen_jobs", label: "Reabre casa" },
];

export function formatWorkdays(workdays, t = (s) => s) {
  if (!workdays || workdays.length === 0) return "—";
  if (workdays.length === 7) return t("Todos os dias");
  return [...workdays]
    .sort()
    .map((d) => {
      const short = WEEKDAYS.find((w) => w.id === d)?.short;
      return short ? t(short) : null;
    })
    .filter(Boolean)
    .join(", ");
}

export default function Collaborators() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState(() => {
    try {
      return localStorage.getItem("cm:collaborators:sort") || "name_asc";
    } catch {
      return "name_asc";
    }
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingHighlight, setEditingHighlight] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const { confirm, ConfirmModal } = useConfirm();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";

  async function load() {
    const [u, p, t] = await Promise.all([
      api.get("/collaborators"),
      api.get("/positions"),
      api.get("/teams"),
    ]);
    setItems(u.data);
    setPositions(p.data);
    setTeams(t.data || []);
  }
  const fetchAll = load;
  useEffect(() => {
    load();
  }, []);

  const positionsByName = useMemo(() => {
    const m = {};
    (positions || []).forEach((p) => {
      if (p?.name) m[p.name] = p;
    });
    return m;
  }, [positions]);

  async function reactivate(u) {
    try {
      await api.patch(`/collaborators/${u.id}`, { status: "ativo" });
      toast.success(t("Colaborador reativado"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const matched = items.filter((u) => {
      if (!showInactive && u.status === "inativo") return false;
      if (!q) return true;
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        (u.position || "").toLowerCase().includes(q)
      );
    });

    const arr = [...matched];
    const collator = new Intl.Collator("en-US", { sensitivity: "base" });
    const ts = (u) => new Date(u.created_at || 0).getTime();
    switch (sortBy) {
      case "name_desc":
        arr.sort((a, b) => collator.compare(b.name || "", a.name || ""));
        break;
      case "newest":
        arr.sort((a, b) => ts(b) - ts(a));
        break;
      case "oldest":
        arr.sort((a, b) => ts(a) - ts(b));
        break;
      case "position":
        arr.sort((a, b) => collator.compare(a.position || "", b.position || ""));
        break;
      case "role":
        arr.sort((a, b) => collator.compare(a.role || "", b.role || ""));
        break;
      case "name_asc":
      default:
        arr.sort((a, b) => collator.compare(a.name || "", b.name || ""));
    }
    return arr;
  }, [items, showInactive, searchQuery, sortBy]);

  useEffect(() => {
    try { localStorage.setItem("cm:collaborators:sort", sortBy); } catch { /* ignore */ }
  }, [sortBy]);

  // Colaboradores aprovados via convite ficam como "inativo" até admin definir cargo/dias.
  const pendingActivation = useMemo(
    () => items.filter((u) => u.status === "inativo"),
    [items],
  );

  // Onboarding em andamento: colaboradores com etapas faltando.
  // (Pula admins, pula quem está realmente completo.)
  const onboardingInProgress = useMemo(() => {
    const teamMembersById = new Map();
    teams.forEach((t) => {
      (t.member_ids || []).forEach((mid) => {
        if (!teamMembersById.has(mid)) teamMembersById.set(mid, []);
        teamMembersById.get(mid).push(t);
      });
    });
    return items
      .filter((u) => u.role !== "admin")
      .map((u) => {
        const missing = [];
        if (!u.position) missing.push("Cargo");
        if (!(u.workdays || []).length) missing.push("Dias");
        if (!teamMembersById.has(u.id)) missing.push("Equipe");
        return { user: u, missing };
      })
      .filter((x) => x.missing.length > 0);
  }, [items, teams]);

  async function remove(u) {
    const ok = await confirm({
      title: t('Desativar colaborador "{name}"?', { name: u.name }),
      description:
        t("O acesso ao sistema será removido e o colaborador não poderá mais ser atribuído a novas equipes ou limpezas. Os dados ficam guardados (pode reativar depois)."),
      actionLabel: t("Desativar"),
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/collaborators/${u.id}`);
      toast.success(t("Colaborador desativado"));
      refreshSidebarCounts();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function hardDelete(u) {
    const ok = await confirm({
      title: t('Excluir permanentemente "{name}"?', { name: u.name }),
      description:
        t("Esta ação é DEFINITIVA. O colaborador será removido das equipes e apagado do banco de dados. As limpezas já realizadas continuam no histórico (com o nome em snapshot)."),
      actionLabel: t("Excluir permanentemente"),
      destructive: true,
      requireText: u.name,
    });
    if (!ok) return;
    try {
      await api.delete(`/collaborators/${u.id}/permanent`);
      toast.success(t("{name} excluído permanentemente", { name: u.name }));
      refreshSidebarCounts();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao excluir"));
    }
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Time")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold">{t("Colaboradores")}</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[44px] px-1 select-none">
            <input
              type="checkbox"
              data-testid="collab-show-inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="w-5 h-5 accent-blue-600 cursor-pointer"
            />
            {t("Mostrar inativos")}
          </label>
          <Button
            data-testid="collab-invite-history-btn"
            variant="outline"
            onClick={() => setHistoryOpen(true)}
          >
            <Clock size={16} className="mr-1" /> {t("Histórico de convites")}
          </Button>
          <Button
            data-testid="collab-invite-btn"
            variant="outline"
            onClick={() => setInviteOpen(true)}
          >
            <Send size={16} className="mr-1" /> {t("Convidar colaborador")}
          </Button>
          <Button
            data-testid="collab-new-btn"
            onClick={() => {
              setEditing({ ...empty });
              setEditingHighlight(false);
              setOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus size={16} className="mr-1" /> {t("Novo colaborador")}
          </Button>
        </div>
      </div>

      <PendingInvitationsBanner
        onApproved={(createdUser) => {
          fetchAll();
          // Garante que o colaborador recém-criado (status=inativo) seja visível
          setShowInactive(true);
          // Se o backend retornou o user criado, já abre o dialog de edição
          // com cargo + dias de trabalho destacados (ativação em 1 fluxo).
          if (createdUser?.id) {
            setEditing({ ...empty, ...createdUser });
            setEditingHighlight(true);
            setOpen(true);
          }
        }}
      />

      {!showInactive && pendingActivation.length > 0 && (
        <div
          data-testid="collab-pending-activation-banner"
          className="mb-4 flex items-start gap-3 p-4 rounded-2xl border border-amber-200 bg-amber-50"
        >
          <div className="shrink-0 w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
            <UserCircle2 size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900 text-sm">
              {pendingActivation.length === 1
                ? t("1 colaborador(a) aguardando ativação")
                : t("{n} colaboradores aguardando ativação", { n: pendingActivation.length })}
            </div>
            <div className="text-xs text-amber-800 mt-0.5 leading-snug">
              {pendingActivation
                .slice(0, 3)
                .map((u) => u.name)
                .filter(Boolean)
                .join(", ")}
              {pendingActivation.length > 3 ? ` ${t("e mais {n}", { n: pendingActivation.length - 3 })}` : ""}
              {" "}{t("— defina cargo e dias de trabalho para ativar.")}
            </div>
          </div>
          <Button
            data-testid="collab-show-pending-btn"
            size="sm"
            variant="outline"
            className="border-amber-300 text-amber-900 hover:bg-amber-100"
            onClick={() => setShowInactive(true)}
          >
            {t("Ver agora")}
          </Button>
        </div>
      )}

      {onboardingInProgress.length > 0 && (
        <div
          data-testid="onboarding-in-progress-card"
          className="mb-6 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50/70 to-white p-5"
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="shrink-0 w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 text-sm">
                {t("Onboarding em andamento ({n})", { n: onboardingInProgress.length })}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t("Continue de onde parou. Cada pessoa abaixo tem etapas faltando.")}
              </div>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {onboardingInProgress.map(({ user: u, missing }) => (
              <li
                key={u.id}
                data-testid={`onboarding-item-${u.id}`}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="shrink-0 w-9 h-9 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center text-slate-400 border border-slate-200">
                  {u.photo_path ? (
                    <img
                      src={fileUrl(u.photo_path)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserCircle2 size={18} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {u.name || t("(sem nome)")}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-0.5">
                    {missing.map((m) => (
                      <span
                        key={m}
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-medium"
                      >
                        {t("Falta")} {t(m)}
                      </span>
                    ))}
                  </div>
                </div>
                <Button
                  size="sm"
                  data-testid={`onboarding-continue-${u.id}`}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
                  onClick={() => {
                    setEditing(u);
                    setEditingHighlight(true);
                    setShowInactive(true);
                    setOpen(true);
                  }}
                >
                  {t("Continuar")}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <WeeklySchedule items={filteredItems} />

      <div className="flex flex-wrap gap-2 mb-4">
        <SearchWithRecent
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t("Localizar por nome, e-mail, telefone ou cargo…")}
          testId="collab-search"
          storageKey="cm:recent:collaborators"
          className="flex-1 sm:min-w-[280px]"
        />
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="collab-sort"
          aria-label={t("Ordenar colaboradores")}
          title={t("Ordenar colaboradores")}
          className="cm-input sm:min-w-[200px] flex-1 sm:flex-initial"
        >
          <option value="name_asc">{t("A → Z (nome)")}</option>
          <option value="name_desc">{t("Z → A (nome)")}</option>
          <option value="newest">{t("Mais recentes (criação)")}</option>
          <option value="oldest">{t("Mais antigos (criação)")}</option>
          <option value="position">{t("Por cargo")}</option>
          <option value="role">{t("Por função (admin/colaborador)")}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredItems.length === 0 && searchQuery && (
          <div className="col-span-full text-center text-slate-500 py-8 text-sm">
            {t("Nenhum colaborador encontrado para")} “{searchQuery}”.
          </div>
        )}
        {filteredItems.map((u) => (
          <div className="cm-card p-5" key={u.id} data-testid={`collab-card-${u.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-28 h-28 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 border border-slate-200 flex-shrink-0">
                {u.photo_path ? (
                  <img
                    src={fileUrl(u.photo_path)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserCircle2 size={56} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate text-base flex items-center gap-2 flex-wrap">
                    {u.name}
                    {u.status === "inativo" && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-normal"
                        title={t("Defina cargo/equipe e reative para liberar o acesso")}
                      >
                        {t("⚠ Aguardando ativação")}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t(ROLE_LABEL[u.role])} · {t(u.status)}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 justify-end">
                  {u.status === "inativo" && (
                    <button
                      onClick={() => reactivate(u)}
                      data-testid={`collab-reactivate-${u.id}`}
                      className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                      title="Reativar"
                    >
                      Reativar
                    </button>
                  )}
                  <button
                    onClick={() => setViewing(u)}
                    data-testid={`collab-view-${u.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                    title={t("Visualizar")}
                  >
                    <Eye size={18} />
                  </button>
                  <button
                    onClick={() => {
                      setEditing(u);
                      setEditingHighlight(u.status === "inativo");
                      setOpen(true);
                    }}
                    data-testid={`collab-edit-${u.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    title={t("Editar")}
                  >
                    <Pencil size={18} />
                  </button>
                  <button
                    onClick={() => remove(u)}
                    data-testid={`collab-delete-${u.id}`}
                    className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                    title={t("Desativar (soft delete)")}
                  >
                    <Trash2 size={18} />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => hardDelete(u)}
                      data-testid={`collab-hard-delete-${u.id}`}
                      className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-red-600 bg-red-600 text-white hover:bg-red-700 transition-colors"
                      title={t("Excluir permanentemente")}
                    >
                      <XCircle size={18} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 mt-3 text-sm">
                  <div>
                    <div className="text-slate-500 text-xs">{t("Telefone")}</div>
                    <div className="truncate">
                      {u.phone ? `+1 ${formatUSPhone(u.phone)}` : (
                        <span className="italic text-slate-400">{t("não informado")}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs">{t("Valor por dia")}</div>
                    <div className="font-semibold">{brl(u.valor_por_dia)}</div>
                  </div>
                  {u.ssn && (
                    <div>
                      <div className="text-slate-500 text-xs">SSN / ITIN</div>
                      <div className="truncate">{u.ssn}</div>
                    </div>
                  )}
                  {u.zelle_venmo && (
                    <div>
                      <div className="text-slate-500 text-xs">Zelle / Venmo</div>
                      <div className="truncate">{u.zelle_venmo}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-slate-500 text-xs">{t("📅 Dias de trabalho")}</div>
              <div className="font-medium text-blue-700">
                {formatWorkdays(u.workdays, t)}
              </div>
            </div>
            {(() => {
              const isPowerRole = u.role === "admin" || u.role === "supervisor";
              const pos = positionsByName[u.position];
              const grantedPerms = isPowerRole
                ? PERM_BADGES
                : PERM_BADGES.filter((p) => pos && pos[p.key]);
              return (
                <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`collab-cargo-${u.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-500 text-xs">{t("Cargo")}</span>
                    <span className="text-sm font-semibold text-slate-800 truncate">
                      {u.position || (
                        <span className="italic font-normal text-slate-400">{t("sem cargo")}</span>
                      )}
                    </span>
                  </div>
                  {isPowerRole ? (
                    <div className="mt-2">
                      <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        {t("Todos os poderes")}
                      </span>
                    </div>
                  ) : grantedPerms.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {grantedPerms.map((p) => (
                        <span
                          key={p.key}
                          className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                        >
                          {t(p.label)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-2 text-[11px] text-slate-400 italic">
                      {t("Sem permissões especiais")}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      <CollabDialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditingHighlight(false);
        }}
        item={editing}
        positions={positions}
        highlightActivation={editingHighlight}
        onSaved={() => {
          setOpen(false);
          setEditingHighlight(false);
          load();
        }}
      />
      <CollabViewDialog user={viewing} onClose={() => setViewing(null)} />
      <InviteCollaboratorDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <InvitationHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
      <CleanupTestUsersDialog
        open={cleanupOpen}
        onOpenChange={setCleanupOpen}
        onCleaned={load}
      />
      {ConfirmModal}
    </AdminLayout>
  );
}

export function CollabViewDialog({ user, onClose }) {
  const { t } = useI18n();
  if (!user) return null;
  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="collab-view-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye size={18} /> {user.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-4 mb-4">
          <div className="w-20 h-20 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400 border border-slate-200">
            {user.photo_path ? (
              <img
                src={fileUrl(user.photo_path)}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <UserCircle2 size={42} />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-xl font-bold truncate">{user.name}</div>
            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
              <ShieldCheck size={12} className="text-emerald-500" />
              {t(ROLE_LABEL[user.role])}
              <span className="mx-1 text-slate-300">·</span>
              <span
                className={`px-1.5 py-0.5 rounded-full text-[10px] border ${
                  user.status === "ativo"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                }`}
              >
                {t(user.status)}
              </span>
            </div>
            {user.position && (
              <div className="text-sm text-slate-700 mt-1">
                {t("Cargo:")} <strong>{user.position}</strong>
              </div>
            )}
            {user.phone && (
              <div className="flex flex-wrap gap-2 mt-2">
                <a
                  href={`tel:+1${String(user.phone).replace(/\D/g, "").slice(-10)}`}
                  data-testid="collab-view-call"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <Phone size={14} /> {t("Ligar")}
                </a>
                <a
                  href={`https://wa.me/1${String(user.phone).replace(/\D/g, "").slice(-10)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="collab-view-whatsapp"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100 transition-colors"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ViewField label={t("E-mail")} icon={<Mail size={13} />}>
            {user.email}
          </ViewField>
          <ViewField label={t("Telefone")} icon={<Phone size={13} />}>
            {user.phone ? `+1 ${formatUSPhone(user.phone)}` : "—"}
          </ViewField>
          <ViewField label="SSN / ITIN">{user.ssn || "—"}</ViewField>
          <ViewField label="Zelle / Venmo">{user.zelle_venmo || "—"}</ViewField>
          <ViewField label={t("Valor por dia")}>
            <strong className="text-emerald-700">
              {brl(user.valor_por_dia || 0)}
            </strong>
          </ViewField>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("Fechar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewField({ label, icon, children }) {
  return (
    <div className="p-3 rounded-lg border border-slate-200 bg-slate-50/50">
      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1 mb-0.5">
        {icon}
        {label}
      </div>
      <div className="text-sm text-slate-800 break-words">{children}</div>
    </div>
  );
}

function CollabDialog({
  open,
  onOpenChange,
  item,
  positions,
  onSaved,
  highlightActivation = false,
}) {
  const { t } = useI18n();
  const [form, setForm] = useState(item || empty);
  const [showPassword, setShowPassword] = useState(false);
  function copyPassword() {
    const pwd = (form.password || "").trim();
    if (!pwd) {
      toast.error(t("Digite ou gere uma senha primeiro"));
      return;
    }
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(pwd)
        .then(() => toast.success(t("Senha copiada")))
        .catch(() => toast.error(t("Não foi possível copiar")));
    }
  }
  function generateStrongPassword() {
    const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
    const lower = "abcdefghijkmnpqrstuvwxyz";
    const digits = "23456789";
    const symbols = "!@#$%&*?";
    const all = upper + lower + digits + symbols;
    const pick = (s) => s[Math.floor(Math.random() * s.length)];
    let pwd = pick(upper) + pick(lower) + pick(digits) + pick(symbols);
    for (let i = 0; i < 8; i++) pwd += pick(all);
    pwd = pwd.split("").sort(() => Math.random() - 0.5).join("");
    set("password", pwd);
    setShowPassword(true);
    if (navigator.clipboard) navigator.clipboard.writeText(pwd).catch(() => {});
    toast.success(t("Senha forte gerada e copiada"));
  }
  const [activationProgress, setActivationProgress] = useState({
    teamAdded: false,
    scheduleGenerated: false,
  });
  useEffect(() => {
    setForm(item || empty);
    setActivationProgress({ teamAdded: false, scheduleGenerated: false });
  }, [item, open]);
  const isEdit = !!item?.id;
  function set(k, v) {
    let val = v;
    if (k === "name") val = titleCase(v);
    else if (k === "email") val = (v || "").toLowerCase();
    setForm((f) => ({ ...f, [k]: val }));
  }
  async function save() {
    try {
      const body = {
        ...form,
        valor_por_dia: Number(form.valor_por_dia) || 0,
      };
      // Se o admin está ativando (mudou cargo + dias estavam preenchidos), garante status=ativo.
      if (highlightActivation && body.position && (body.workdays || []).length) {
        body.status = "ativo";
      }
      if (isEdit) {
        const passwordChanged = !!body.password;
        if (!body.password) delete body.password;
        await api.patch(`/collaborators/${item.id}`, body);
        toast.success(
          passwordChanged
            ? t("Colaborador salvo · senha atualizada")
            : t("Colaborador salvo"),
        );
      } else {
        await api.post("/collaborators", body);
        toast.success(t("Colaborador salvo"));
      }
      refreshSidebarCounts();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const f = form;
  const positionMissing = highlightActivation && !f.position;
  const workdaysMissing = highlightActivation && (!f.workdays || f.workdays.length === 0);
  const ringPosition = positionMissing
    ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-white rounded-md"
    : "";
  const ringWorkdays = workdaysMissing
    ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-white rounded-md p-1"
    : "";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="collab-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("Editar colaborador") : t("Novo colaborador")}
          </DialogTitle>
        </DialogHeader>

        {highlightActivation ? (
          <>
            <ActivationStepper
              steps={[
                {
                  key: "data",
                  label: "Dados",
                  done: !!(f.name || "").trim() && !!(f.email || "").trim(),
                  current: false,
                },
                {
                  key: "role",
                  label: "Cargo + dias",
                  done: !!f.position && (f.workdays || []).length > 0,
                  current:
                    !!(f.name || "").trim() &&
                    !!(f.email || "").trim() &&
                    (!f.position || (f.workdays || []).length === 0),
                },
                {
                  key: "team",
                  label: t("Equipe"),
                  done: activationProgress.teamAdded,
                  current:
                    !!f.position &&
                    (f.workdays || []).length > 0 &&
                    !activationProgress.teamAdded,
                },
                {
                  key: "schedule",
                  label: t("Agenda"),
                  done: activationProgress.scheduleGenerated,
                  current:
                    activationProgress.teamAdded &&
                    !activationProgress.scheduleGenerated,
                },
              ]}
            />
            <div
              data-testid="collab-activation-banner"
              className="mb-4 flex items-start gap-3 p-3 rounded-xl border border-amber-200 bg-amber-50"
            >
              <Sparkles size={18} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900 leading-snug">
                {t("Para ativar esta pessoa, complete as etapas acima — defina cargo e dias de trabalho destacados em amarelo abaixo, escolha uma equipe, agende a primeira semana e clique em Salvar.")}
              </div>
            </div>
          </>
        ) : (
          <CollabFormProgress form={f} isEdit={isEdit} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Lbl label={t("Nome")}>
            <input
              data-testid="collab-field-name"
              className="cm-input"
              value={f.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Lbl>
          <Lbl label={t("E-mail")}>
            <input
              data-testid="collab-field-email"
              className="cm-input"
              type="email"
              value={f.email}
              onChange={(e) => set("email", e.target.value)}
            />
            {isEdit && (
              <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                <span>⚠</span>
                <span>{t("Alterar o e-mail também muda o login do colaborador.")}</span>
              </p>
            )}
          </Lbl>
          <Lbl label={isEdit ? t("Senha (deixe vazio para manter)") : t("Senha")}>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  data-testid="collab-field-password"
                  className="cm-input pr-10 w-full"
                  type={showPassword ? "text" : "password"}
                  value={f.password || ""}
                  onChange={(e) => set("password", e.target.value)}
                />
                <button
                  type="button"
                  data-testid="collab-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t("Ocultar senha") : t("Mostrar senha")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <button
                type="button"
                data-testid="collab-copy-password"
                onClick={copyPassword}
                disabled={!(f.password || "").trim()}
                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 text-xs font-semibold px-3"
                title={t("Copiar senha")}
              >
                <Copy size={14} /> {t("Copiar")}
              </button>
              <button
                type="button"
                data-testid="collab-generate-password"
                onClick={generateStrongPassword}
                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-800 text-xs font-semibold px-3"
                title={t("Gerar senha forte")}
              >
                <Sparkles size={14} /> {t("Gerar")}
              </button>
            </div>
            {isEdit && (
              <div className="text-[11px] text-slate-500 mt-1">
                {t("O campo fica em branco por segurança. Digite uma nova senha para trocar; deixe vazio para manter a atual.")}
              </div>
            )}
          </Lbl>
          <Lbl label={t("Cargo / Função")}>
            <select
              data-testid="collab-field-position"
              className={`cm-input ${ringPosition}`}
              value={f.position || ""}
              onChange={(e) => set("position", e.target.value)}
            >
              <option value="">{t("— selecione —")}</option>
              {positions
                .filter((p) => p.status === "ativo")
                .map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))}
            </select>
          </Lbl>
          <Lbl label={t("Perfil")}>
            <select
              data-testid="collab-field-role"
              className="cm-input"
              value={f.role}
              onChange={(e) => set("role", e.target.value)}
            >
              <option value="collaborator">{t("Colaborador(a)")}</option>
              <option value="supervisor">{t("Supervisão")}</option>
              <option value="admin">{t("Administrador")}</option>
            </select>
          </Lbl>
          <Lbl label="Status">
            <select
              className="cm-input"
              value={f.status}
              onChange={(e) => set("status", e.target.value)}
            >
              <option value="ativo">{t("Ativo")}</option>
              <option value="inativo">{t("Inativo")}</option>
            </select>
          </Lbl>
          <Lbl label={t("Telefone (US)")}>
            <PhoneInput
              testId="collab-field-phone"
              value={f.phone || ""}
              onChange={(v) => set("phone", v)}
            />
          </Lbl>
          <Lbl label="SSN / ITIN Number">
            <input
              className="cm-input"
              value={f.ssn || ""}
              onChange={(e) => set("ssn", e.target.value)}
            />
          </Lbl>
          <Lbl label="Valor por dia (US$)">
            <input
              data-testid="collab-field-value"
              className="cm-input"
              type="number"
              step="0.01"
              value={f.valor_por_dia}
              onChange={(e) => set("valor_por_dia", e.target.value)}
            />
          </Lbl>
          <Lbl label="Zelle / Venmo">
            <input
              className="cm-input"
              value={f.zelle_venmo || ""}
              onChange={(e) => set("zelle_venmo", e.target.value)}
              placeholder={t("@usuario, telefone ou e-mail")}
            />
          </Lbl>
          <div className="md:col-span-2">
            <USAddressFields
              value={
                typeof f.address === "object" && f.address
                  ? f.address
                  : { street: f.address || "" }
              }
              onChange={(v) => set("address", v)}
              testIdPrefix="collab-addr"
            />
          </div>
          <div className="md:col-span-2">
            <Lbl label={t("Dias de trabalho na semana")}>
              <div className={ringWorkdays}>
                <WorkdaysSelector
                  value={f.workdays || []}
                  onChange={(v) => set("workdays", v)}
                />
              </div>
            </Lbl>
          </div>
          <div className="md:col-span-2">
            <Lbl label={t("Foto")}>
              <FileUpload
                folder="collaborators"
                value={f.photo_path ? [f.photo_path] : []}
                multiple={false}
                onChange={(v) => set("photo_path", v[0] || "")}
                testId="collab-photo"
              />
            </Lbl>
          </div>
        </div>

        {highlightActivation && isEdit && f.position && (f.workdays || []).length > 0 && (
          <TeamSuggestion
            userId={item.id}
            userName={f.name}
            userPhone={f.phone}
            onTeamAdded={() =>
              setActivationProgress((p) => ({ ...p, teamAdded: true }))
            }
            onScheduleGenerated={(count) =>
              setActivationProgress((p) => ({
                ...p,
                scheduleGenerated: count > 0,
              }))
            }
          />
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancelar")}
          </Button>
          <Button
            onClick={save}
            data-testid="collab-save-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {t("Salvar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CollabFormProgress — usa FormProgress shared
// ─────────────────────────────────────────────────────────────────────────
function CollabFormProgress({ form, isEdit }) {
  const { t } = useI18n();
  const items = [
    { key: "name", label: t("Nome"), ok: !!(form.name || "").trim() },
    { key: "email", label: t("E-mail"), ok: !!(form.email || "").trim() && (form.email || "").includes("@") },
    { key: "password", label: t("Senha"), ok: isEdit ? true : (form.password || "").length >= 6 },
    { key: "role", label: t("Cargo de acesso"), ok: !!form.role },
    { key: "phone", label: t("Telefone"), ok: !!(form.phone || "").trim() },
  ];
  return <FormProgress items={items} testId="collab-form-progress" />;
}


function Lbl({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function TeamSuggestion({ userId, userName, userPhone, onTeamAdded, onScheduleGenerated }) {
  const { t } = useI18n();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // team_id being added
  const [lastAddedTeam, setLastAddedTeam] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null); // { count, teamName, jobs }
  const [skippedTeam, setSkippedTeam] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api
      .get(`/teams/suggest-for-user/${userId}`)
      .then((r) => setSuggestions(r.data || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, [userId]);

  async function addTo(team) {
    setAdding(team.id);
    try {
      await api.post(`/teams/${team.id}/add-member/${userId}`);
      toast.success(`${userName || t("Colaborador")} ${t("adicionado à equipe")} ${team.name}`);
      setLastAddedTeam(team);
      setSuggestions((prev) => prev.filter((x) => x.id !== team.id));
      onTeamAdded?.(team);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao adicionar à equipe"));
    } finally {
      setAdding(null);
    }
  }

  async function generateSchedule() {
    if (!lastAddedTeam) return;
    setGenerating(true);
    try {
      const r = await api.post(`/teams/${lastAddedTeam.id}/auto-schedule-week`, {
        user_id: userId,
        days_ahead: 7,
        start_hour: 9,
      });
      const created = r.data?.created_jobs || [];
      if (created.length === 0) {
        toast.info(
          r.data?.skipped_reason ||
            t("Nenhuma limpeza nova para gerar (todas já agendadas ou equipe sem clientes)."),
        );
      } else {
        toast.success(t("{n} limpeza(s) agendada(s) para os próximos 7 dias", { n: created.length }));
      }
      setGenerated({ count: created.length, teamName: lastAddedTeam.name, jobs: created });
      onScheduleGenerated?.(created.length);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Falha ao gerar escala");
    } finally {
      setGenerating(false);
    }
  }

  function buildWhatsAppMessage() {
    const firstName = (userName || "").split(" ")[0] || "tudo bem";
    const teamName = generated?.teamName || lastAddedTeam?.name || "nossa equipe";
    const jobs = generated?.jobs || [];
    const lines = [
      `Olá, ${firstName}! 👋`,
      "",
      `Bem-vindo(a) à *Clean Manager USA*! Você foi adicionado(a) à equipe *${teamName}*.`,
    ];
    if (jobs.length > 0) {
      lines.push("", "📅 *Sua agenda dos próximos 7 dias:*");
      jobs.forEach((j) => {
        const d = new Date(j.scheduled_at);
        const dia = d.toLocaleDateString("en-US", {
          weekday: "short",
          day: "2-digit",
          month: "2-digit",
        });
        const hora = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
        lines.push(`• ${dia} às ${hora} — ${j.client_name || "Cliente"}`);
      });
    }
    lines.push(
      "",
      "Baixe o app no celular pra ver detalhes, fazer check-in com GPS e enviar fotos antes/depois.",
      "Qualquer dúvida, estou aqui! 🙌",
    );
    return lines.join("\n");
  }

  function notifyWhatsApp() {
    const digits = String(userPhone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(buildWhatsAppMessage());
    const url = digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, "_blank", "noopener");
  }

  function skipTeamStep() {
    setSkippedTeam(true);
    onTeamAdded?.(null);
  }

  function skipScheduleStep() {
    setGenerated({ count: 0, teamName: lastAddedTeam.name, jobs: [], skipped: true });
    onScheduleGenerated?.(1); // marca etapa como concluída
  }

  if (loading) return null;

  // Após adicionar a uma equipe, oferece geração de escala
  if (lastAddedTeam) {
    return (
      <div
        data-testid="schedule-suggestion-block"
        className="mt-5 p-4 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <CheckCircle2 size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              {t("Adicionado(a) à equipe")} <span className="text-emerald-700">{lastAddedTeam.name}</span>
            </div>
            {generated ? (
              <div className="text-xs text-slate-600 mt-0.5">
                {generated.count > 0
                  ? t("{n} limpeza(s) agendada(s) nos próximos 7 dias para {team}.", { n: generated.count, team: generated.teamName })
                  : t("Nenhuma limpeza nova para gerar (já cobertas ou equipe sem clientes).")}
                {generated.count > 0 && (
                  <button
                    type="button"
                    data-testid="notify-whatsapp-btn"
                    onClick={notifyWhatsApp}
                    className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white text-xs font-semibold transition-colors"
                  >
                    <svg
                      viewBox="0 0 32 32"
                      width="13"
                      height="13"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M16.045 28.45c-2.21 0-4.422-.625-6.32-1.79l-.83-.487-4.4 1.122 1.18-4.244-.518-.83a11.948 11.948 0 0 1-1.835-6.367c.015-6.604 5.39-11.977 12.038-11.977 3.207 0 6.224 1.262 8.494 3.534 2.27 2.27 3.535 5.29 3.52 8.488-.014 6.604-5.39 11.978-12.038 11.978zm10.21-22.21A14.27 14.27 0 0 0 16.045 2c-7.927 0-14.39 6.45-14.404 14.39 0 2.534.66 5.01 1.925 7.198L1.5 31l7.595-1.992a14.453 14.453 0 0 0 6.937 1.762h.014c7.927 0 14.39-6.45 14.404-14.39 0-3.84-1.49-7.453-4.205-10.156z" />
                    </svg>
                    {t("Avisar pelo WhatsApp")}
                  </button>
                )}
              </div>
            ) : (
              <div className="text-xs text-slate-500 mt-0.5">
                {t("Quer já agendar a primeira semana de limpezas com base nos dias de trabalho dele(a)?")}
              </div>
            )}
          </div>
          {!generated && (
            <div className="flex flex-col gap-1.5 items-end">
              <Button
                size="sm"
                data-testid="generate-week-schedule-btn"
                onClick={generateSchedule}
                disabled={generating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
              >
                <Calendar size={14} className="mr-1" />
                {generating ? t("Agendando…") : t("Agendar semana")}
              </Button>
              <button
                type="button"
                data-testid="skip-schedule-step-btn"
                onClick={skipScheduleStep}
                className="text-[11px] text-slate-500 hover:text-slate-900 hover:underline"
              >
                {t("Pular — agendar depois")}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (skippedTeam) return null;
  if (suggestions.length === 0) return null;
  const best = suggestions[0];

  return (
    <div
      data-testid="team-suggestion-block"
      className="mt-5 p-4 rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
          <Layers size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {t("Sugestão: adicionar à equipe")} <span className="text-blue-700">{best.name}</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {best.member_count === 0
              ? t("Equipe sem membros ainda — boa para começar.")
              : t("{n} membro(s) atualmente — equipe com mais capacidade.", { n: best.member_count })}
          </div>
          {suggestions.length > 1 && (
            <div className="text-[11px] text-slate-400 mt-1">
              {t("Outras opções:")} {suggestions.slice(1).map((x) => x.name).join(", ")}
            </div>
          )}
          <button
            type="button"
            data-testid="skip-team-step-btn"
            onClick={skipTeamStep}
            className="text-[11px] text-slate-500 hover:text-slate-900 hover:underline mt-2"
          >
            {t("Pular esta etapa — configurar depois")}
          </button>
        </div>
        <div className="flex flex-col gap-1.5">
          {suggestions.slice(0, 2).map((tm) => (
            <Button
              key={tm.id}
              size="sm"
              variant={tm.id === best.id ? "default" : "outline"}
              data-testid={`team-suggest-add-${tm.id}`}
              onClick={() => addTo(tm)}
              disabled={adding === tm.id}
              className={
                tm.id === best.id
                  ? "bg-blue-600 hover:bg-blue-700 text-white whitespace-nowrap"
                  : "whitespace-nowrap"
              }
            >
              <UserPlus size={14} className="mr-1" />
              {adding === tm.id ? t("Adicionando…") : tm.name}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}


function WorkdaysSelector({ value, onChange }) {
  const { t } = useI18n();
  const set = new Set(value || []);
  function toggle(id) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(Array.from(next).sort());
  }
  return (
    <div
      className="flex flex-wrap gap-2"
      data-testid="workdays-selector"
    >
      {WEEKDAYS.map((w) => {
        const active = set.has(w.id);
        return (
          <button
            type="button"
            key={w.id}
            data-testid={`workday-${w.id}`}
            onClick={() => toggle(w.id)}
            className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl border-2 text-xs font-semibold transition-all ${
              active
                ? "border-blue-600 bg-blue-600 text-white shadow-md scale-105"
                : "border-slate-200 bg-white text-slate-500 hover:border-blue-300 hover:text-slate-700"
            }`}
            title={w.label}
          >
            <span>{w.short}</span>
          </button>
        );
      })}
    </div>
  );
}

function WeeklySchedule({ items }) {
  const { t } = useI18n();
  const activeOnly = items.filter((u) => u.status === "ativo");
  if (activeOnly.length === 0) return null;
  return (
    <div className="cm-card p-5 mb-6" data-testid="weekly-schedule">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          {t("📅 Schedule semanal da equipe")}
        </h2>
        <span className="text-xs text-slate-500">
          {activeOnly.length} {t("colaboradores ativos")}
        </span>
      </div>
      <ScheduleRow
        title={t("Esta semana")}
        activeOnly={activeOnly}
        weekOffset={0}
        testIdPrefix="weekday"
      />
    </div>
  );
}

function ScheduleRow({ title, activeOnly, weekOffset, testIdPrefix }) {
  const { t } = useI18n();
  const today = new Date();
  // Start of this week (Sunday)
  const weekStart = new Date(today);
  weekStart.setDate(today.getDate() - today.getDay() + weekOffset * 7);
  weekStart.setHours(0, 0, 0, 0);
  const MONTH_ABBR = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
        {title}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {WEEKDAYS.map((day) => {
          const working = activeOnly.filter((u) =>
            (u.workdays || []).includes(day.id),
          );
          const isToday = weekOffset === 0 && today.getDay() === day.id;
          const dayDate = new Date(weekStart);
          dayDate.setDate(weekStart.getDate() + day.id);
          const dateLabel = `${t(MONTH_ABBR[dayDate.getMonth()])} ${dayDate.getDate()}`;
          return (
            <div
              key={day.id}
              data-testid={`${testIdPrefix}-col-${day.id}`}
              className={`rounded-xl border-2 p-3 ${
                isToday
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-200 bg-slate-50"
              }`}
            >
              <div
                className={`text-xs font-bold uppercase tracking-wide mb-2 flex items-center justify-between gap-1 ${isToday ? "text-blue-700" : "text-slate-500"}`}
              >
                <span>{dateLabel}</span>
                {isToday && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-600 text-white">
                    {t("hoje")}
                  </span>
                )}
              </div>
              {working.length === 0 ? (
                <div className="text-xs text-slate-400 italic">{t("Ninguém")}</div>
              ) : (
                <ul className="space-y-1.5">
                  {working.map((u) => (
                    <li
                      key={u.id}
                      data-testid={`${testIdPrefix}-${day.id}-collab-${u.id}`}
                      className="text-xs flex items-center gap-1.5 truncate"
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                      <span className="truncate text-slate-700">{u.name}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-2 text-[10px] text-slate-400">
                {working.length}{" "}
                {working.length === 1 ? t("pessoa") : t("pessoas")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Self-onboarding de colaboradores via link/código de convite
// ════════════════════════════════════════════════════════════════════════

function inviteUrl(token) {
  return `${window.location.origin}/invite/${token}`;
}

function fmtRelTime(iso, t = (s) => s) {
  if (!iso) return "—";
  const date = new Date(iso);
  const diff = Date.now() - date.getTime();
  const hrs = Math.round(diff / 3600000);
  if (hrs < 1) return t("agora");
  if (hrs < 24) return t("{n}h atrás", { n: hrs });
  return t("{n}d atrás", { n: Math.round(hrs / 24) });
}

/**
 * Banner exibido no topo da página Colaboradores quando há convites SUBMITTED.
 * Permite aprovar (pré-preenchido) ou rejeitar diretamente.
 */
function PendingInvitationsBanner({ onApproved }) {
  const { t } = useI18n();
  const [pending, setPending] = useState([]);
  const [reviewing, setReviewing] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/collaborator-invitations?status=submitted");
      setPending(data);
    } catch {}
  }
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  if (pending.length === 0) return null;

  return (
    <>
      <div
        className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4"
        data-testid="pending-invitations-banner"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Clock size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-amber-900">
              {pending.length}{" "}
              {pending.length > 1
                ? t("convites aguardando aprovação")
                : t("convite aguardando aprovação")}
            </div>
            <div className="text-xs text-amber-800/80 mt-0.5">
              {t("Colaboradores que enviaram seus dados via link de convite.")}
            </div>
            <ul className="mt-3 space-y-2">
              {pending.map((inv) => {
                const d = inv.submitted_data || {};
                return (
                  <li
                    key={inv.id}
                    className="bg-white border border-amber-200 rounded-lg p-3 flex flex-wrap items-center gap-2"
                    data-testid={`pending-invite-${inv.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{d.name || t("Sem nome")}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {d.email} · {fmtRelTime(inv.submitted_at, t)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      data-testid={`invite-review-${inv.id}`}
                      onClick={() => setReviewing(inv)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <CheckCircle2 size={14} className="mr-1" /> {t("Revisar e aprovar")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`invite-reject-${inv.id}`}
                      onClick={async () => {
                        if (!window.confirm(t("Rejeitar este convite?"))) return;
                        try {
                          await api.post(`/collaborator-invitations/${inv.id}/reject`);
                          toast.success(t("Convite rejeitado"));
                          load();
                        } catch (e) {
                          toast.error(formatApiError(e.response?.data?.detail));
                        }
                      }}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                    >
                      <XCircle size={14} />
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      {reviewing && (
        <ReviewInvitationDialog
          invitation={reviewing}
          onClose={() => setReviewing(null)}
          onApproved={(createdUser) => {
            setReviewing(null);
            load();
            onApproved?.(createdUser);
          }}
        />
      )}
    </>
  );
}

function ReviewInvitationDialog({ invitation, onClose, onApproved }) {
  const { t } = useI18n();
  const d = invitation.submitted_data || {};
  const [form, setForm] = useState({
    name: d.name || "",
    email: d.email || "",
    phone: d.phone || "",
    address: d.address || "",
    ssn: d.ssn || "",
    zelle_venmo: d.zelle_venmo || "",
    photo_path: d.photo_path || "",
    role: "collaborator",
  });
  const [saving, setSaving] = useState(false);

  async function approve() {
    setSaving(true);
    try {
      const { data: createdUser } = await api.post(
        `/collaborator-invitations/${invitation.id}/approve`,
        form,
      );
      toast.success(t("Convite aprovado! Defina cargo e dias de trabalho abaixo."));
      onApproved(createdUser);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao aprovar"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Revisar dados do colaborador")}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2 mb-2">
          {t("Confira os dados enviados. Após aprovar, o colaborador será criado como inativo — defina o cargo e a equipe antes de ativar o acesso.")}
        </p>

        <div className="space-y-3">
          <Lbl label={t("Nome")}>
            <input className="cm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Lbl>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Lbl label={t("E-mail")}>
              <input className="cm-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Lbl>
            <Lbl label={t("Telefone")}>
              <PhoneInput
                value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
              />
            </Lbl>
          </div>
          <Lbl label={t("Endereço")}>
            <input className="cm-input" value={typeof form.address === "string" ? form.address : (form.address?.street || "")} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Lbl>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Lbl label="SSN">
              <input className="cm-input" value={form.ssn} onChange={(e) => setForm({ ...form, ssn: e.target.value })} />
            </Lbl>
            <Lbl label="Zelle/Venmo">
              <input className="cm-input" value={form.zelle_venmo} onChange={(e) => setForm({ ...form, zelle_venmo: e.target.value })} />
            </Lbl>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
          <Button onClick={approve} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? t("Aprovando…") : t("Aprovar e criar colaborador")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Dialog para gerar e compartilhar um link de convite.
 */
function InviteCollaboratorDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [invite, setInvite] = useState(null);
  const [creating, setCreating] = useState(false);
  const [recipientName, setRecipientName] = useState("");

  async function generate() {
    setCreating(true);
    try {
      const trimmed = recipientName.trim();
      const { data } = await api.post(
        "/collaborator-invitations",
        trimmed ? { recipient_name: trimmed } : {},
      );
      setInvite(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("Falha ao gerar convite"));
    } finally {
      setCreating(false);
    }
  }

  useEffect(() => {
    if (!open) {
      setInvite(null);
      setRecipientName("");
    }
  }, [open]);

  function copy(text, label) {
    navigator.clipboard.writeText(text);
    toast.success(`${label} ${t("copiado")}`);
  }

  const url = invite ? inviteUrl(invite.token) : "";
  const greetingName = invite?.recipient_name?.split(" ")[0] || "";
  const waMsg = encodeURIComponent(
    `Olá${greetingName ? `, ${greetingName}` : ""}! Preencha seus dados para entrar na equipe da Clean Manager USA: ${url}\n\n` +
      `Ou use o código: ${invite?.short_code}`,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("Convidar colaborador")}</DialogTitle>
        </DialogHeader>
        {!invite ? (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              {t("Para qual colaborador é este convite? O nome ajuda você a identificar no histórico.")}
            </p>
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">
                {t("Nome do colaborador (opcional)")}
              </label>
              <input
                data-testid="invite-recipient-name"
                className="cm-input"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder={t("Ex.: João Silva")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !creating) generate();
                }}
              />
            </div>
            <Button
              data-testid="invite-generate-btn"
              onClick={generate}
              disabled={creating}
              className="w-full"
            >
              {creating ? t("Gerando convite…") : t("Gerar convite")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              {invite.recipient_name ? (
                <>
                  {t("Convite para")} <strong>{invite.recipient_name}</strong>.{" "}
                  {t("Envie o link ou código abaixo. Validade:")} <strong>{t("7 dias")}</strong>.
                </>
              ) : (
                <>
                  {t("Envie este link ou código para o colaborador. Ele preenche os dados sozinho e você aprova depois. Validade:")}{" "}
                  <strong>{t("7 dias")}</strong>.
                </>
              )}
            </p>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("Link do convite")}</div>
              <div className="flex gap-2">
                <input
                  data-testid="invite-link-url"
                  readOnly
                  value={url}
                  className="cm-input flex-1 text-xs font-mono"
                  onFocus={(e) => e.target.select()}
                />
                <Button size="sm" variant="outline" onClick={() => copy(url, t("Link do convite"))} data-testid="invite-copy-url">
                  <Copy size={14} />
                </Button>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("Código curto")}</div>
              <div className="flex gap-2">
                <code
                  data-testid="invite-short-code"
                  className="flex-1 cm-input font-mono text-center text-lg tracking-widest bg-slate-50 select-all cursor-pointer"
                  onClick={() => copy(invite.short_code, t("Código"))}
                >
                  {invite.short_code}
                </code>
                <Button size="sm" variant="outline" onClick={() => copy(invite.short_code, t("Código"))}>
                  <Copy size={14} />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              <a
                href={`https://wa.me/?text=${waMsg}`}
                target="_blank"
                rel="noreferrer"
                data-testid="invite-send-wa"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
              >
                <Send size={14} /> WhatsApp
              </a>
              <a
                href={`mailto:?subject=Convite Clean Manager USA&body=${waMsg}`}
                data-testid="invite-send-email"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
              >
                <Mail size={14} /> E-mail
              </a>
            </div>
          </div>
        )}
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("Fechar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ════════════════════════════════════════════════════════════════════════
// Histórico completo de convites (todos os status)
// ════════════════════════════════════════════════════════════════════════

const STATUS_INFO = {
  pending: { label: "Aguardando preenchimento", color: "bg-blue-50 text-blue-700 border-blue-200" },
  submitted: { label: "Aguardando aprovação", color: "bg-amber-50 text-amber-800 border-amber-200" },
  approved: { label: "Aprovado", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejeitado", color: "bg-red-50 text-red-700 border-red-200" },
  expired: { label: "Expirado", color: "bg-slate-100 text-slate-600 border-slate-200" },
  revoked: { label: "Revogado", color: "bg-slate-100 text-slate-500 border-slate-200" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function InvitationHistoryDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/collaborator-invitations");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) load();
  }, [open]);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = items.reduce((acc, i) => {
    acc[i.status] = (acc[i.status] || 0) + 1;
    return acc;
  }, {});

  async function revoke(id) {
    if (!window.confirm("Revogar este convite? O link deixará de funcionar.")) return;
    try {
      await api.delete(`/collaborator-invitations/${id}`);
      toast.success("Convite revogado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  function copyUrl(token) {
    const url = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado");
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("Histórico de convites")}</DialogTitle>
        </DialogHeader>

        {/* Filtros */}
        <div className="flex flex-wrap gap-1.5 my-3">
          {[
            ["all", t("Todos"), items.length],
            ["pending", t("Pendentes"), counts.pending || 0],
            ["submitted", t("Aguardando"), counts.submitted || 0],
            ["approved", t("Aprovados"), counts.approved || 0],
            ["rejected", t("Rejeitados"), counts.rejected || 0],
            ["expired", t("Expirados"), counts.expired || 0],
            ["revoked", t("Revogados"), counts.revoked || 0],
          ].map(([key, label, count]) => (
            <button
              key={key}
              data-testid={`invite-filter-${key}`}
              onClick={() => setFilter(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filter === key
                  ? "bg-blue-600 border-blue-600 text-white"
                  : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"
              }`}
            >
              {label} <span className="opacity-70">({count})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-500 text-sm">{t("Carregando…")}</div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-slate-500 text-sm">
            {t("Nenhum convite")} {filter !== "all" ? `${t("com status")} "${t(STATUS_INFO[filter]?.label)}"` : ""}.
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((inv) => {
              const st = STATUS_INFO[inv.status] || STATUS_INFO.pending;
              const d = inv.submitted_data || {};
              const displayName = d.name || inv.recipient_name || null;
              return (
                <li
                  key={inv.id}
                  data-testid={`invite-history-${inv.id}`}
                  className="border border-slate-200 rounded-lg p-3 bg-white"
                >
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {displayName ? (
                          <span className="text-sm font-semibold text-slate-900 truncate">
                            {displayName}
                          </span>
                        ) : (
                          <span className="text-sm italic text-slate-500">
                            (sem nome cadastrado)
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${st.color}`}>
                          {st.label}
                        </span>
                        <span className="font-mono text-[11px] text-slate-400">
                          #{inv.short_code}
                        </span>
                      </div>
                      {d.name && d.email && (
                        <div className="text-xs text-slate-500 mt-0.5 truncate">
                          {d.email}
                        </div>
                      )}
                      <div className="text-[11px] text-slate-500 mt-1">
                        {t("Criado:")} {fmtDateTime(inv.created_at)}
                        {inv.submitted_at && ` · ${t("Preenchido:")} ${fmtDateTime(inv.submitted_at)}`}
                        {inv.status === "pending" && ` · ${t("Expira:")} ${fmtDateTime(inv.expires_at)}`}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {inv.status === "pending" && (
                        <>
                          <button
                            onClick={() => copyUrl(inv.token)}
                            data-testid={`invite-history-copy-${inv.id}`}
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-700"
                            title={t("Copiar link")}
                          >
                            <Copy size={14} />
                          </button>
                          <button
                            onClick={() => revoke(inv.id)}
                            data-testid={`invite-history-revoke-${inv.id}`}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-700"
                            title={t("Revogar")}
                          >
                            <XCircle size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("Fechar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function CleanupTestUsersDialog({ open, onOpenChange, onCleaned }) {
  const [tab, setTab] = useState("users"); // "users" | "clients"
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selUsers, setSelUsers] = useState(() => new Set());
  const [selClients, setSelClients] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelUsers(new Set());
    setSelClients(new Set());
    setLoading(true);
    Promise.all([
      api.get("/admin/test-users").then((r) => r.data || []).catch(() => []),
      api.get("/admin/test-clients").then((r) => r.data || []).catch(() => []),
    ])
      .then(([u, c]) => {
        setUsers(u);
        setClients(c);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const isUsersTab = tab === "users";
  const items = isUsersTab ? users : clients;
  const selected = isUsersTab ? selUsers : selClients;
  const setSelected = isUsersTab ? setSelUsers : setSelClients;

  function toggleAll(checked) {
    setSelected(checked ? new Set(items.map((u) => u.id)) : new Set());
  }
  function toggleOne(id) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  async function handleCleanup() {
    if (selected.size === 0) {
      toast.error(`Selecione pelo menos um ${isUsersTab ? "usuário" : "cliente"}`);
      return;
    }
    setSubmitting(true);
    try {
      const ids = Array.from(selected);
      const url = isUsersTab
        ? "/admin/cleanup-test-users"
        : "/admin/cleanup-test-clients";
      const r = await api.post(url, { ids });
      const n = r.data?.deleted_count ?? 0;
      const label = isUsersTab ? "usuário" : "cliente";
      toast.success(`${n} ${label}${n === 1 ? "" : "s"} de teste removido${n === 1 ? "" : "s"}`);
      if (!isUsersTab && r.data?.deleted_jobs) {
        toast.info(`${r.data.deleted_jobs} limpeza(s) associada(s) também removidas`);
      }
      if (r.data?.skipped?.length) {
        toast.info(`${r.data.skipped.length} ignorado(s) (não bateu com padrão de teste)`);
      }
      onOpenChange(false);
      onCleaned?.();
      refreshSidebarCounts();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  }

  const allChecked = items.length > 0 && selected.size === items.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="cleanup-test-users-dialog"
      >
        <DialogHeader>
          <DialogTitle>Limpar dados de teste</DialogTitle>
        </DialogHeader>

        <div className="text-sm text-slate-600 mb-3">
          Registros abaixo foram identificados como criados por agentes de teste
          automatizados (e-mail/nome com padrões como <code>@example.com</code>,{" "}
          <code>test_</code>, <code>UI Tester</code>, <code>Patched</code>,{" "}
          <code>TEST_</code>). A exclusão é <strong>definitiva</strong>.
        </div>

        <div className="flex gap-2 mb-3 border-b border-slate-200">
          <button
            type="button"
            data-testid="cleanup-tab-users"
            onClick={() => setTab("users")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              isUsersTab
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            Colaboradores ({users.length})
          </button>
          <button
            type="button"
            data-testid="cleanup-tab-clients"
            onClick={() => setTab("clients")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              !isUsersTab
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            Clientes ({clients.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center text-slate-500 py-8">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-slate-500 py-8" data-testid="cleanup-empty">
            Nenhum {isUsersTab ? "usuário" : "cliente"} de teste encontrado.
          </div>
        ) : (
          <>
            <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer">
              <input
                type="checkbox"
                data-testid="cleanup-select-all"
                checked={allChecked}
                onChange={(e) => toggleAll(e.target.checked)}
              />
              Selecionar todos ({items.length})
            </label>
            <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
              {items.map((u) => (
                <li key={u.id} className="flex items-center gap-3 p-3">
                  <input
                    type="checkbox"
                    data-testid={`cleanup-row-${u.id}`}
                    checked={selected.has(u.id)}
                    onChange={() => toggleOne(u.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{u.name || "(sem nome)"}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {u.email || "—"}
                      {isUsersTab ? ` · ${ROLE_LABEL[u.role] || u.role} · ${u.status}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            data-testid="cleanup-confirm-btn"
            onClick={handleCleanup}
            disabled={submitting || selected.size === 0 || items.length === 0}
            className="bg-red-600 hover:bg-red-700"
          >
            <Trash2 size={16} className="mr-1" />
            Excluir selecionados ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

